import * as React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Label from "../../../components/label";
import { useBitrixContext } from "../../../api/bitrix/context/BitrixContext";
import { useSharedContext } from "../../../shared/SharedComponentsContext";
import MenuPopover from "../../../components/menu-popover";
import { IconButton, MenuItem } from "@mui/material";
import Iconify from "../../../components/iconify";
import ConfirmDialog from "../../../components/confirm-dialog";
import { dbCompany } from "../../../api/parnerus/types/company";

type Props = {
  row: dbCompany;
  onDeleteRow?: VoidFunction;
};

export default function CompaniesCard({ row, onDeleteRow }: Props) {
  const { openCompanyViewSlider, openCompanyEditSlider } = useSharedContext();
  const { userfieldList } = useBitrixContext();
  const {
    TITLE,
    UF_IRS_STATUS,
    UF_STATE_OF_INCORPORATION,
    primaryContactId,
    contact,
  } = row;

  const [openConfirm, setOpenConfirm] = React.useState(false);

  const [openPopover, setOpenPopover] = React.useState<HTMLElement | null>(
    null
  );

  const handleOpenConfirm = () => {
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
  };

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setOpenPopover(event.currentTarget);
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  const irsStatus = userfieldList
    .find((u) => u.FIELD_NAME === "UF_CRM_1787074855")
    ?.LIST.find((v) => v.ID === UF_IRS_STATUS)?.VALUE;

  const stateOfIncorporation = userfieldList
    .find((u) => u.FIELD_NAME === "UF_CRM_1787074949")
    ?.LIST.find((v) => v.ID === UF_STATE_OF_INCORPORATION)?.VALUE;

  return (
    <Box sx={{ minWidth: 275 }}>
      <Card
        variant="outlined"
        sx={{ mt: 2, minHeight: 210, position: "relative" }}
        onClick={() => openCompanyViewSlider(row)}
      >
        <Box sx={{ position: "absolute", top: 8, right: 8 }}>
          <IconButton
            color={openPopover ? "inherit" : "default"}
            onClick={handleOpenPopover}
          >
            <Iconify icon="eva:more-vertical-fill" />
          </IconButton>
        </Box>
        <React.Fragment>
          <CardContent>
            <Typography
              gutterBottom
              sx={{ color: "text.secondary", fontSize: 14 }}
            >
              Empresa
            </Typography>
            <Typography variant="h5" component="div">
              {TITLE}
            </Typography>
            {primaryContactId && contact && (
              <Typography sx={{ color: "text.secondary", mt: 1.5, mb: 1.5 }}>
                {contact.NAME} {contact.LAST_NAME}
              </Typography>
            )}

            {irsStatus && (
              <Label
                variant="soft"
                color={(UF_IRS_STATUS === "69" && "error") || "success"}
                sx={{ textTransform: "capitalize", mr: 2 }}
              >
                {irsStatus}
              </Label>
            )}
            {stateOfIncorporation && (
              <Label
                variant="soft"
                color="success"
                sx={{ textTransform: "capitalize" }}
              >
                {stateOfIncorporation}
              </Label>
            )}
          </CardContent>
        </React.Fragment>
      </Card>

      <MenuPopover
        open={openPopover}
        onClose={handleClosePopover}
        arrow="right-top"
        sx={{ width: 140 }}
      >
        <MenuItem
          onClick={() => {
            handleClosePopover();
            openCompanyViewSlider(row);
          }}
        >
          <Iconify icon="clarity:details-line" />
          Detalles
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleClosePopover();
            openCompanyEditSlider(row);
          }}
        >
          <Iconify icon="eva:edit-fill" />
          Editar
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleOpenConfirm();
            handleClosePopover();
          }}
          sx={{ color: "error.main" }}
        >
          <Iconify icon="eva:trash-2-outline" />
          Eliminar
        </MenuItem>
      </MenuPopover>

      <ConfirmDialog
        open={openConfirm}
        onClose={handleCloseConfirm}
        title="Eliminar"
        content="¿Estás seguro que deseas eliminar este registro? Esto deshabilitará el registro, y sólo quedará visible para los registros que estén relacionado al mismo, pero no podrás encontrarlo más en este listado"
        action={
          <Button variant="contained" color="error" onClick={onDeleteRow}>
            Eliminar
          </Button>
        }
      />
    </Box>
  );
}
